import React, { useState, useEffect, useMemo } from 'react';
import { Container, Table, Button, Modal, Form, Alert, Spinner, Row, Col, InputGroup, Card } from 'react-bootstrap';
import api from '../../api/axios';
import { useLanguage } from '../../contexts/LanguageContext';
import { useNotification } from '../../contexts/NotificationContext';

const AdminCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [draggedSubcategoryId, setDraggedSubcategoryId] = useState(null);
  const [dragOverMainCategoryId, setDragOverMainCategoryId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');
  const [formData, setFormData] = useState({
    category_type: 'main',
    name: '',
    slug: '',
    description: '',
    manual_url: '',
    image_url: '',
    parent_id: '',
    display_order: 0,
    is_visible: true
  });
  const { t } = useLanguage();
  const { notify, confirm } = useNotification();

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    setSelectedCategoryIds((prev) => prev.filter((id) => categories.some((category) => category.id === id)));
  }, [categories]);

  const parentNameById = useMemo(() => {
    return new Map(categories.map((category) => [category.id, category.name]));
  }, [categories]);

  const filteredCategories = useMemo(() => {
    const value = searchTerm.trim().toLowerCase();
    if (!value) {
      return categories;
    }

    return categories.filter((category) => {
      const haystack = [
        category.name,
        category.slug,
        category.description,
        parentNameById.get(category.parent_id),
        category.is_visible ? 'visible' : 'hidden'
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(value);
    });
  }, [categories, searchTerm, parentNameById]);

  const displayedCategories = useMemo(() => {
    const matchedCategoryIds = new Set(filteredCategories.map((category) => category.id));
    const childrenByParentId = categories.reduce((acc, category) => {
      if (!category.parent_id) {
        return acc;
      }

      const existingChildren = acc.get(category.parent_id) || [];
      existingChildren.push(category);
      acc.set(category.parent_id, existingChildren);
      return acc;
    }, new Map());

    return categories.reduce((acc, category) => {
      if (category.category_type !== 'main') {
        return acc;
      }

      const matchingChildren = (childrenByParentId.get(category.id) || []).filter((child) => matchedCategoryIds.has(child.id));
      const shouldShowParent = matchedCategoryIds.has(category.id) || matchingChildren.length > 0;

      if (!shouldShowParent) {
        return acc;
      }

      acc.push(category);
      acc.push(...matchingChildren);
      return acc;
    }, []);
  }, [categories, filteredCategories]);

  const allFilteredSelected = displayedCategories.length > 0
    && displayedCategories.every((category) => selectedCategoryIds.includes(category.id));

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/admin/categories');
      setCategories(response.data);
      setError(null);
    } catch (err) {
      setError(t('common.error'));
      console.error('Failed to fetch categories:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleShowModal = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        category_type: category.category_type || 'main',
        name: category.name,
        slug: category.slug,
        description: category.description || '',
        manual_url: category.manual_url || '',
        image_url: category.image_url || '',
        parent_id: category.parent_id || '',
        display_order: category.display_order || 0,
        is_visible: category.is_visible
      });
      setImagePreviewUrl(category.image_url || '');
    } else {
      setEditingCategory(null);
      setFormData({
        category_type: 'main',
        name: '',
        slug: '',
        description: '',
        manual_url: '',
        image_url: '',
        parent_id: '',
        display_order: 0,
        is_visible: true
      });
      setImagePreviewUrl('');
    }
    setSelectedImageFile(null);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCategory(null);
    setSelectedImageFile(null);
    setImagePreviewUrl('');
  };

  const handleImageFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setSelectedImageFile(file);

    if (!file) {
      setImagePreviewUrl(formData.image_url || '');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setImagePreviewUrl(String(reader.result || ''));
    };
    reader.readAsDataURL(file);
  };

  const uploadCategoryImage = async (categoryId, file) => {
    const body = new FormData();
    body.append('image', file);

    const response = await api.post(`/api/admin/categories/${categoryId}/image`, body, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    return response.data?.image_url || '';
  };

  const handleDeleteImage = async () => {
    if (!editingCategory) {
      setSelectedImageFile(null);
      setImagePreviewUrl('');
      setFormData((previous) => ({ ...previous, image_url: '' }));
      return;
    }

    try {
      await api.delete(`/api/admin/categories/${editingCategory.id}/image`);
      setSelectedImageFile(null);
      setImagePreviewUrl('');
      setFormData((previous) => ({ ...previous, image_url: '' }));
      notify({ variant: 'success', message: 'Category image removed' });
      fetchCategories();
    } catch (err) {
      notify({ variant: 'danger', message: err.response?.data?.error || t('common.error') });
    }
  };

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData((previous) => ({
      ...previous,
      [e.target.name]: value,
      ...(e.target.name === 'category_type' && value === 'main' ? { parent_id: '' } : {})
    }));

    // Auto-generate slug from name if creating new category
    if (e.target.name === 'name' && !editingCategory) {
      const slug = e.target.value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      setFormData(prev => ({
        ...prev,
        slug
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      let categoryId;

      if (editingCategory) {
        await api.put(`/api/admin/categories/${editingCategory.id}`, formData);
        categoryId = editingCategory.id;
      } else {
        const response = await api.post('/api/admin/categories', formData);
        categoryId = response.data?.categoryId;
      }

      let nextImageUrl = formData.image_url || '';
      if (selectedImageFile && categoryId) {
        nextImageUrl = await uploadCategoryImage(categoryId, selectedImageFile);
      }

      setFormData((previous) => ({
        ...previous,
        image_url: nextImageUrl
      }));

      notify({
        variant: 'success',
        message: editingCategory ? 'Category updated successfully' : 'Category created successfully'
      });

      fetchCategories();

      handleCloseModal();
    } catch (err) {
      notify({
        variant: 'danger',
        message: err.response?.data?.error || t('common.error')
      });
      console.error('Failed to save category:', err);
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await confirm({
      title: 'Delete category',
      message: 'Are you sure you want to delete this category? This action cannot be undone.',
      confirmText: 'Delete'
    });

    if (!confirmed) return;

    try {
      await api.delete(`/api/admin/categories/${id}`);
      notify({ variant: 'success', message: 'Category deleted successfully' });
      fetchCategories();
    } catch (err) {
      notify({
        variant: 'danger',
        message: err.response?.data?.error || t('common.error')
      });
      console.error('Failed to delete category:', err);
    }
  };

  const toggleCategorySelection = (categoryId) => {
    setSelectedCategoryIds((prev) => (
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    ));
  };

  const toggleSelectAllFilteredCategories = () => {
    if (allFilteredSelected) {
      const filteredIds = new Set(displayedCategories.map((category) => category.id));
      setSelectedCategoryIds((prev) => prev.filter((id) => !filteredIds.has(id)));
      return;
    }

    setSelectedCategoryIds((prev) => Array.from(new Set([
      ...prev,
      ...displayedCategories.map((category) => category.id)
    ])));
  };

  const handleBulkDelete = async () => {
    if (selectedCategoryIds.length === 0) {
      notify({ variant: 'warning', message: 'Select at least one category first' });
      return;
    }

    const confirmed = await confirm({
      title: 'Bulk delete categories',
      message: `Are you sure you want to delete ${selectedCategoryIds.length} selected categories?`,
      confirmText: 'Delete'
    });

    if (!confirmed) {
      return;
    }

    try {
      setBulkLoading(true);
      const response = await api.post('/api/admin/categories/bulk-delete', {
        ids: selectedCategoryIds
      });
      const result = response.data;
      const errorCount = result.errors?.length || 0;

      notify({
        variant: errorCount > 0 ? 'warning' : 'success',
        message: `Processed ${result.processed} categories${errorCount > 0 ? `, ${errorCount} errors` : ''}`
      });

      setError(errorCount > 0
        ? result.errors.map((item) => `#${item.id}: ${item.error}`).join(' | ')
        : null);
      setSelectedCategoryIds([]);
      fetchCategories();
    } catch (err) {
      notify({
        variant: 'danger',
        message: err.response?.data?.error || t('common.error')
      });
    } finally {
      setBulkLoading(false);
    }
  };

  const getParentCategories = () => {
    return categories.filter((cat) => cat.category_type === 'main');
  };

  const handleMoveSubcategory = async (subcategoryId, newParentId) => {
    try {
      await api.put(`/api/admin/categories/${subcategoryId}`, { parent_id: newParentId });
      notify({ variant: 'success', message: 'Subcategory moved successfully' });
      fetchCategories();
    } catch (err) {
      notify({ variant: 'danger', message: err.response?.data?.error || t('common.error') });
    }
  };

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleExportCategories = async () => {
    try {
      const response = await api.get('/api/admin/categories/export', {
        responseType: 'blob'
      });
      downloadBlob(new Blob([response.data]), 'categories-export.xlsx');
      notify({ variant: 'success', message: 'Categories exported successfully' });
    } catch (err) {
      notify({
        variant: 'danger',
        message: err.response?.data?.error || 'Failed to export categories'
      });
    }
  };

  const handleDownloadCategoryTemplate = async () => {
    try {
      const response = await api.get('/api/admin/categories/import-template', {
        responseType: 'blob'
      });
      downloadBlob(new Blob([response.data]), 'category-import-template.xlsx');
      notify({ variant: 'success', message: 'Template downloaded successfully' });
    } catch (err) {
      notify({
        variant: 'danger',
        message: err.response?.data?.error || 'Failed to download template'
      });
    }
  };

  const handleImportCategories = async () => {
    if (!importFile) {
      notify({ variant: 'warning', message: 'Please select a file to import' });
      return;
    }

    try {
      setImporting(true);
      const body = new FormData();
      body.append('file', importFile);

      const response = await api.post('/api/admin/categories/import', body, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setImportResults(response.data);
      setImportFile(null);
      fetchCategories();
      notify({ variant: 'success', message: 'Category import completed' });
    } catch (err) {
      notify({
        variant: 'danger',
        message: err.response?.data?.error || 'Failed to import categories'
      });
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <Container className="loading-spinner">
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>{t('admin.categories')}</h1>
        <div className="d-flex gap-2">
          <Button variant="outline-success" onClick={handleExportCategories} title={t('admin.exportExcel')} aria-label={t('admin.exportExcel')} style={{fontSize:'1.75rem'}}>
            📊
          </Button>
          <Button variant="outline-primary" onClick={() => {
            setShowImportModal(true);
            setImportResults(null);
          }} title={t('admin.importExcel')} aria-label={t('admin.importExcel')} style={{fontSize:'1.75rem'}}>
            📥
          </Button>
          <Button variant="primary" onClick={() => handleShowModal()} title={t('admin.addCategory')} aria-label={t('admin.addCategory')} style={{fontSize:'1.75rem'}}>
            ⊕
          </Button>
        </div>
      </div>
      
      {error && <Alert variant="danger">{error}</Alert>}

      <Card className="mb-3">
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={8}>
              <Form.Label>{t('admin.filterCategories')}</Form.Label>
              <InputGroup>
                <InputGroup.Text>Search</InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder={`${t('admin.filterCategories')}...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={4}>
              <div className="small text-muted mb-2">{t('admin.selected')} {selectedCategoryIds.length}</div>
              <Button
                variant="danger"
                className="w-100"
                onClick={handleBulkDelete}
                disabled={bulkLoading || selectedCategoryIds.length === 0}
              >
                {bulkLoading ? t('admin.deleting') : t('admin.deleteSelected')}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      
      <Table responsive striped bordered hover>
        <thead>
          <tr>
            <th style={{ width: '44px' }}>
              <Form.Check
                type="checkbox"
                checked={allFilteredSelected}
                onChange={toggleSelectAllFilteredCategories}
                aria-label="Select all filtered categories"
              />
            </th>
            <th>ID</th>
            <th>Type</th>
            <th>Name</th>
            <th>Slug</th>
            <th>Parent</th>
            <th>Image</th>
            <th>Order</th>
            <th>Visible</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {displayedCategories.map((category) => {
            const isSub = category.category_type === 'sub';
            const isMain = category.category_type === 'main';
            const isDraggingThis = draggedSubcategoryId === category.id;
            const isDropTarget = isMain && dragOverMainCategoryId === category.id;

            return (
            <tr
              key={category.id}
              draggable={isSub}
              title={isSub ? 'Drag to move to another main category' : undefined}
              onDragStart={isSub ? (e) => {
                setDraggedSubcategoryId(category.id);
                e.dataTransfer.effectAllowed = 'move';
              } : undefined}
              onDragEnd={isSub ? () => {
                setDraggedSubcategoryId(null);
                setDragOverMainCategoryId(null);
              } : undefined}
              onDragOver={isMain ? (e) => {
                if (draggedSubcategoryId === null) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                if (dragOverMainCategoryId !== category.id) setDragOverMainCategoryId(category.id);
              } : undefined}
              onDragLeave={isMain ? (e) => {
                if (!e.relatedTarget || !e.currentTarget.contains(e.relatedTarget)) {
                  setDragOverMainCategoryId(null);
                }
              } : undefined}
              onDrop={isMain ? async (e) => {
                e.preventDefault();
                const id = draggedSubcategoryId;
                setDraggedSubcategoryId(null);
                setDragOverMainCategoryId(null);
                if (id === null) return;
                const sub = categories.find((c) => c.id === id);
                if (!sub || sub.parent_id === category.id) return;
                await handleMoveSubcategory(id, category.id);
              } : undefined}
              style={{
                opacity: isDraggingThis ? 0.4 : 1,
                backgroundColor: isDropTarget ? '#d1e7dd' : undefined,
                outline: isDropTarget ? '2px dashed #0d6efd' : undefined,
                cursor: isSub ? (isDraggingThis ? 'grabbing' : 'grab') : undefined,
              }}
            >
              <td>
                <Form.Check
                  type="checkbox"
                  checked={selectedCategoryIds.includes(category.id)}
                  onChange={() => toggleCategorySelection(category.id)}
                  aria-label={`Select category ${category.name}`}
                />
              </td>
              <td>{category.id}</td>
              <td>{category.category_type === 'sub' ? 'Subcategory' : 'Main category'}</td>
              <td>{category.parent_id && '  └─ '}{category.name}</td>
              <td>{category.slug}</td>
              <td>
                {category.parent_id 
                  ? parentNameById.get(category.parent_id) || '-'
                  : '-'
                }
              </td>
              <td>
                {category.image_url ? (
                  <img src={category.image_url} alt={category.name} style={{ width: '48px', height: '48px', objectFit: 'contain', backgroundColor: '#f8f9fa', borderRadius: '6px' }} />
                ) : '-'}
              </td>
              <td>{category.display_order}</td>
              <td>{category.is_visible ? '✓' : '✗'}</td>
              <td>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => handleShowModal(category)}
                  className="me-2"
                >
                  {t('common.edit')}
                </Button>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => handleDelete(category.id)}
                >
                  {t('common.delete')}
                </Button>
              </td>
            </tr>
            );
          })}
        </tbody>
      </Table>

      {displayedCategories.length === 0 && (
        <Alert variant="info">{t('products.noResults')}</Alert>
      )}

      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingCategory ? t('admin.editCategory') : t('admin.addCategory')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Category Type *</Form.Label>
              <Form.Select
                name="category_type"
                value={formData.category_type}
                onChange={handleChange}
                disabled={Boolean(editingCategory)}
              >
                <option value="main">Main category</option>
                <option value="sub">Subcategory</option>
              </Form.Select>
              {editingCategory && (
                <Form.Text className="text-muted">
                  Category type cannot be changed after creation.
                </Form.Text>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Name *</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Slug * (URL-friendly, unique)</Form.Label>
              <Form.Control
                type="text"
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                pattern="[a-z0-9-]+"
                title="Only lowercase letters, numbers, and hyphens"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleChange}
              />
            </Form.Group>

            {formData.category_type === 'sub' && (
              <Form.Group className="mb-3">
                <Form.Label>Manual URL</Form.Label>
                <Form.Control
                  type="url"
                  name="manual_url"
                  value={formData.manual_url}
                  onChange={handleChange}
                  placeholder="https://..."
                />
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Image URL</Form.Label>
              <Form.Control
                type="text"
                name="image_url"
                value={formData.image_url}
                onChange={handleChange}
                placeholder="https://..."
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Upload Image</Form.Label>
              <Form.Control
                type="file"
                accept="image/*,.webp"
                onChange={handleImageFileChange}
              />
              {imagePreviewUrl && (
                <div className="mt-2 d-flex align-items-center gap-2">
                  <img src={imagePreviewUrl} alt="Category preview" style={{ width: '72px', height: '72px', objectFit: 'contain', backgroundColor: '#f8f9fa', borderRadius: '8px' }} />
                  <Button variant="outline-danger" size="sm" type="button" onClick={handleDeleteImage}>
                    Remove image
                  </Button>
                </div>
              )}
            </Form.Group>

            {formData.category_type === 'sub' && (
              <Form.Group className="mb-3">
                <Form.Label>Main Category *</Form.Label>
                <Form.Select
                  name="parent_id"
                  value={formData.parent_id}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select main category</option>
                  {getParentCategories()
                    .filter((cat) => !editingCategory || cat.id !== editingCategory.id)
                    .map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                </Form.Select>
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Display Order</Form.Label>
              <Form.Control
                type="number"
                name="display_order"
                value={formData.display_order}
                onChange={handleChange}
              />
              <Form.Text className="text-muted">
                Lower numbers appear first
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                name="is_visible"
                label="Visible to customers"
                checked={formData.is_visible}
                onChange={handleChange}
              />
            </Form.Group>

            <div className="d-flex justify-content-end">
              <Button variant="secondary" onClick={handleCloseModal} className="me-2">
                {t('common.cancel')}
              </Button>
              <Button variant="primary" type="submit">
                {editingCategory ? t('admin.update') : 'Create'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      <Modal show={showImportModal} onHide={() => setShowImportModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t('admin.importExcel')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted">
            Import categories and subcategories in one file. Existing rows are matched by ID or slug and updated.
          </p>

          <div className="d-grid mb-3">
            <Button variant="outline-secondary" onClick={handleDownloadCategoryTemplate}>
              Download Category Import Template
            </Button>
          </div>

          <Form.Group className="mb-3">
            <Form.Label>Select Excel file</Form.Label>
            <Form.Control
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => setImportFile(e.target.files?.[0] || null)}
            />
          </Form.Group>

          {importResults && (
            <Alert variant={importResults.errors?.length > 0 ? 'warning' : 'success'}>
              <div>Import Results:</div>
              <ul className="mb-0 mt-2">
                <li>Created: {importResults.created || 0}</li>
                <li>Updated: {importResults.updated || 0}</li>
                <li>Errors: {importResults.errors?.length || 0}</li>
              </ul>
              {importResults.errors?.length > 0 && (
                <div className="mt-2">
                  <strong>Error details:</strong>
                  <ul className="mb-0">
                    {importResults.errors.map((item, idx) => (
                      <li key={`${item.row || 'row'}-${idx}`}>
                        Row {item.row || '?'}: {item.error || 'Unknown error'}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowImportModal(false);
            setImportFile(null);
            setImportResults(null);
          }}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={handleImportCategories} disabled={importing || !importFile}>
            {importing ? 'Importing...' : t('admin.importExcel')}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AdminCategories;
